import { Icon, Menu } from "materia"
import {
  type ChangeEvent,
  type ClipboardEvent,
  type DragEvent,
  useEffect,
  useId,
  useLayoutEffect,
  useRef,
  useState,
  type KeyboardEvent,
} from "react"
import { useTranslation } from "react-i18next"
import { modifiers } from "ui-tools"
import type { UploadOutcome } from "../../api"
import { MAX_MESSAGE_LENGTH } from "../../constants"
import type { AttachmentRef } from "../../types"
import { ComposerImage } from "./ComposerImage"
import { draftFromOutcome, type ImageDraft } from "./imageDraft"
import s from "./Composer.module.scss"

type ComposerProps = {
  /** The conversation's name, which the placeholder addresses. */
  conversationName: string
  /**
   * Called with the trimmed body, and the image if one is ready. The body is
   * non-empty unless there is an image.
   */
  onSend: (body: string, attachment?: AttachmentRef) => void
  /** Every edit of the draft, as typed. */
  onDraftChange?: (draft: string) => void
  /** Open the product picker (FAME-237). Omit to hide the + menu. */
  onShareProduct?: () => void
  /** Upload a picked image for this conversation. Omit to offer no images. */
  onUploadImage?: (
    file: File,
    onProgress: (fraction: number) => void
  ) => Promise<UploadOutcome>
  /** Nothing can be sent, and `note` says why. */
  isDisabled?: boolean
  /** No session yet, or reconnecting: the composer waits under a spinner. */
  isOffline?: boolean
  note?: string
}

export function Composer({
  conversationName,
  onSend,
  onDraftChange,
  onShareProduct,
  onUploadImage,
  isDisabled = false,
  isOffline = false,
  note,
}: ComposerProps) {
  const { t } = useTranslation()
  const [draft, setDraft] = useState("")
  const input = useRef<HTMLTextAreaElement>(null)
  const noteId = useId()

  const image = useImage(onUploadImage)
  const fileInput = useRef<HTMLInputElement>(null)

  const body = draft.trim()
  const isBlocked = isDisabled || isOffline
  const ready =
    image.current?.draft.phase === "clean" ? image.current.draft : undefined
  // A picked image must finish uploading clean before anything sends with it.
  const canSend =
    !isBlocked && (image.current ? Boolean(ready) : body.length > 0)

  const send = () => {
    if (!canSend) return

    onSend(body, ready?.attachment)
    setDraft("")
    image.clear()
  }

  const onPick = (event: ChangeEvent<HTMLInputElement>) => {
    if (event.target.files) image.take(event.target.files)
    // Cleared, so picking the same file again still fires a change.
    event.target.value = ""
  }

  const canAttach = Boolean(onUploadImage) && !isBlocked

  // A pasted image uploads straight away. Text wins when the clipboard has
  // both, as office apps put a picture of copied text beside the text itself.
  const onPaste = (event: ClipboardEvent<HTMLTextAreaElement>) => {
    const { types, files } = event.clipboardData
    if (!canAttach || types.includes("text/plain")) return
    if (image.take(files)) event.preventDefault()
  }

  // Claiming the drag is what lets the drop land here instead of the browser
  // opening the file.
  const onDragOver = (event: DragEvent) => {
    if (canAttach && event.dataTransfer.types.includes("Files"))
      event.preventDefault()
  }

  const onDrop = (event: DragEvent) => {
    if (canAttach && image.take(event.dataTransfer.files))
      event.preventDefault()
  }

  const hasActions = Boolean(onShareProduct || onUploadImage)

  // Grow with the content, up to the max-height the stylesheet sets. Runs after
  // the textarea's value reflects `draft`, so clearing it (on send) collapses
  // back to one line instead of measuring the stale height.
  // biome-ignore lint/correctness/useExhaustiveDependencies: re-measure on every draft change
  useLayoutEffect(() => {
    const field = input.current
    if (!field) return

    field.style.height = "auto"
    field.style.height = `${field.scrollHeight}px`
  }, [draft])

  const onKeyDown = (event: KeyboardEvent<HTMLTextAreaElement>) => {
    // Enter sends; shift-enter is how you get a second line.
    if (event.key !== "Enter" || event.shiftKey) return

    event.preventDefault()
    send()
  }

  return (
    // biome-ignore lint/a11y/noStaticElementInteractions: dropping is a shortcut; the + menu is the accessible way to attach
    <div className={s.composer} onDragOver={onDragOver} onDrop={onDrop}>
      {image.current && (
        <ComposerImage
          // One instance per picked image, so nothing it learned carries over.
          key={image.current.previewUrl}
          previewUrl={image.current.previewUrl}
          draft={image.current.draft}
          onRemove={image.clear}
          onRetry={image.retry}
        />
      )}

      <div
        className={modifiers(s, "composer__field", {
          hasActions,
        })}
      >
        {hasActions && (
          <Menu>
            {/* asChild: the + is the trigger, so it keeps the composer's own
                styling while the menu gets headless focus/escape handling. */}
            <Menu.Trigger asChild={true}>
              <button
                className={s.composer__plus}
                type="button"
                disabled={isBlocked}
                aria-label={String(t("chat.SHARE_MENU_ARIA"))}
              >
                <Icon icon="Plus" />
              </button>
            </Menu.Trigger>

            {/* Opens upward — the composer sits at the bottom of the thread. */}
            <Menu.Content anchor="top start" className={s.composer__menu}>
              {/* Passing a class replaces Menu.Item's own: the row is styled here. */}
              {onUploadImage && (
                <Menu.Item
                  as="button"
                  type="button"
                  className={s.composer__menuItem}
                  onClick={() => fileInput.current?.click()}
                >
                  <span className={s.composer__menuIcon}>
                    <Icon icon="Attachment" />
                  </span>
                  <span className={s.composer__menuText}>
                    <span className={s.composer__menuLabel}>
                      {t("chat.SHARE_IMAGE")}
                    </span>
                    <span className={s.composer__menuHint}>
                      {t("chat.SHARE_IMAGE_HINT")}
                    </span>
                  </span>
                </Menu.Item>
              )}
              {onShareProduct && (
                <Menu.Item
                  as="button"
                  type="button"
                  className={s.composer__menuItem}
                  onClick={onShareProduct}
                >
                  <span className={s.composer__menuIcon}>
                    <Icon icon="Stack" />
                  </span>
                  <span className={s.composer__menuText}>
                    <span className={s.composer__menuLabel}>
                      {t("chat.SHARE_PRODUCT")}
                    </span>
                    <span className={s.composer__menuHint}>
                      {t("chat.SHARE_PRODUCT_HINT")}
                    </span>
                  </span>
                </Menu.Item>
              )}
            </Menu.Content>
          </Menu>
        )}

        {onUploadImage && (
          <input
            ref={fileInput}
            type="file"
            accept={IMAGE_TYPES}
            hidden
            onChange={onPick}
          />
        )}

        <textarea
          ref={input}
          className={s.composer__input}
          value={draft}
          rows={1}
          // The server enforces this too; capping here is about the typing,
          // not about trust.
          maxLength={MAX_MESSAGE_LENGTH}
          placeholder={String(
            t("chat.MESSAGE_PLACEHOLDER", { name: conversationName })
          )}
          aria-describedby={note ? noteId : undefined}
          disabled={isOffline}
          onChange={(event) => {
            setDraft(event.target.value)
            onDraftChange?.(event.target.value)
          }}
          onKeyDown={onKeyDown}
          onPaste={onPaste}
        />

        <button
          className={s.composer__send}
          type="button"
          disabled={!canSend}
          onClick={send}
          aria-label={String(t("chat.SEND_ARIA"))}
        >
          <Icon icon="ArrowUp" />
        </button>

        {isOffline && (
          <div
            className={s.composer__offline}
            role="status"
            aria-label={String(t("chat.OFFLINE_NOTE"))}
          >
            <span className={s.composer__spinner} />
          </div>
        )}
      </div>

      {note && (
        <p className={s.composer__note} id={noteId}>
          {note}
        </p>
      )}
    </div>
  )
}

/** The image types the picker offers; HEIF is how some browsers label HEIC. */
const IMAGE_TYPES = "image/jpeg,image/png,image/heic,image/heif"

type PickedImage = { file: File; previewUrl: string; draft: ImageDraft }

/**
 * The image picked for the next message, and its upload. Only the latest
 * upload may update the draft: one that finishes after the image was removed
 * or replaced is ignored.
 *
 * `take` uploads the first image among `files`, else the first file, so the
 * server can say why it is refused. False when there are no files.
 */
function useImage(upload: ComposerProps["onUploadImage"]): {
  current: PickedImage | undefined
  take: (files: FileList) => boolean
  retry: () => void
  clear: () => void
} {
  const [current, setCurrent] = useState<PickedImage>()
  const latest = useRef(0)

  // The preview's object URL lives exactly as long as the picked image.
  useEffect(() => {
    const url = current?.previewUrl
    return () => {
      if (url) URL.revokeObjectURL(url)
    }
  }, [current?.previewUrl])

  const start = (file: File) => {
    if (!upload) return
    const attempt = ++latest.current
    const update = (draft: ImageDraft) => {
      if (attempt === latest.current) setCurrent((c) => c && { ...c, draft })
    }

    upload(file, (progress) => update({ phase: "uploading", progress }))
      .then((outcome) => update(draftFromOutcome(outcome)))
      .catch(() => update({ phase: "failed" }))
  }

  return {
    current,
    take: (files) => {
      const all = Array.from(files)
      const file = all.find((f) => f.type.startsWith("image/")) ?? all[0]
      if (!file) return false
      setCurrent({
        file,
        previewUrl: URL.createObjectURL(file),
        draft: { phase: "uploading", progress: 0 },
      })
      start(file)
      return true
    },
    retry: () => {
      if (!current) return
      setCurrent({ ...current, draft: { phase: "uploading", progress: 0 } })
      start(current.file)
    },
    clear: () => {
      latest.current++
      setCurrent(undefined)
    },
  }
}
